import type { PlanUsage } from "@/common/types/planUsage";
import { type FileData, getFiles } from "@/services/files";
import { create } from "zustand";
import { t } from "i18next";
import { getAvailableMinutes } from "@/services/minutes";

export interface DashboardState {
  files: FileData[];
  loading: boolean;
  error: string | null;
  planUsage?: PlanUsage;

  getRecordedVisits: () => number;
  getTotalDuration: () => number;
  getRecordedMinutes: () => number;
  getMinutesSaved: () => number;
  getAvailablePlanMinutes: () => number;
  getAvailableAdditionalMinutes: () => number;

  getFiles: ({ userId }: { userId: number }) => Promise<void>;
  setFiles: (files: FileData[]) => void;
  fetchAndSetAvailableMinutes: (userId: number) => Promise<void>;
}

export const useDashboardStore = create<DashboardState>()((set, get) => ({
  files: [],
  loading: false,
  error: null,
  planUsage: undefined,

  getRecordedVisits: (): number => {
    return get().files.length;
  },
  // TODO: we should change the way we get this data
  getTotalDuration: (): number => {
    return get().files.reduce((sum, file) => {
      const duration = parseInt(file.duration?.toString() || "0", 10);
      return sum + duration;
    }, 0);
  },

  getRecordedMinutes: (): number => Math.floor(get().getTotalDuration() / 60),

  getMinutesSaved: (): number => Math.floor(get().getTotalDuration() / 20),

  getAvailablePlanMinutes: (): number => get().planUsage?.availableMinutes || 0,
  getAvailableAdditionalMinutes: (): number =>
    get().planUsage?.availableAdditionalMinutes || 0,

  setFiles: (files: FileData[]): void => {
    set({ files });
  },

  getFiles: async ({ userId }: { userId: number }): Promise<void> => {
    set({ loading: true, error: null });
    try {
      const { files, error } = await getFiles({ userId: userId, sparse: 1 });
      if (!error) {
        set({ files: files, loading: false });
        return;
      } else {
        set({ error: t("error.unknown"), files: [], loading: false });
        return;
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        set({ files: [], error: error.message, loading: false });
      } else {
        set({
          files: [],
          error: t("error.unknown"),
          loading: false,
        });
      }
    }
  },

  fetchAndSetAvailableMinutes: async (userId: number): Promise<void> => {
    const userMinutesData = await getAvailableMinutes(userId);
    set({ planUsage: userMinutesData });
  },
}));
